// API utility functions for making authenticated requests

class ApiClient {
    constructor() {
        this.baseURL = '/api/v1'
        this.authToken = localStorage.getItem('auth_token')
        this.isRefreshing = false
        this.failedQueue = []
    }

    setAuthToken(token) {
        this.authToken = token
        if (token) {
            localStorage.setItem('auth_token', token)
            // Store token metadata for expiry checking
            localStorage.setItem('auth_token_set_at', Date.now().toString())
        } else {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_token_set_at')
        }
    }

    getAuthHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        }

        if (this.authToken) {
            headers['X-Auth-Token'] = this.authToken
        }

        // Add CSRF token for non-GET requests
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken
        }

        return headers
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`
        const config = {
            headers: this.getAuthHeaders(),
            ...options,
        }

        try {
            const response = await fetch(url, config)
            
            if (!response.ok) {
                if (response.status === 401) {
                    return this.handle401Error(endpoint, options)
                }
                
                const errorData = await response.json().catch(() => ({}))
                
                // Handle validation errors (422 status with errors object)
                if (response.status === 422 && errorData.errors) {
                    // Convert Rails validation errors to readable format
                    const validationMessages = []
                    for (const [field, messages] of Object.entries(errorData.errors)) {
                        if (Array.isArray(messages)) {
                            validationMessages.push(...messages.map(msg => 
                                field === 'base' ? msg : `${field} ${msg}`
                            ))
                        } else {
                            validationMessages.push(`${field} ${messages}`)
                        }
                    }
                    throw new Error(validationMessages.join(', '))
                }
                
                throw new Error(errorData.error || `HTTP ${response.status}`)
            }

            // Handle 204 No Content responses (no JSON to parse)
            if (response.status === 204) {
                return null
            }
            
            return await response.json()
        } catch (error) {
            // If it's a network error and not a 401 retry, just throw
            if (error.message === 'Authentication required' || error.message.includes('401')) {
                throw error
            }
            // Don't log expected 403 "Admin access required" errors (expected for non-admin users)
            if (!error.message.includes('Admin access required')) {
                console.error(`API request failed: ${endpoint}`, error)
            }
            throw error
        }
    }

    // Handle 401 errors with automatic token refresh and retry
    async handle401Error(endpoint, options) {
        if (this.isRefreshing) {
            // If we're already refreshing, queue this request
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject, endpoint, options })
            })
        }

        this.isRefreshing = true

        try {
            // Try to refresh the token
            await this.refreshToken()
            
            // Process any queued requests
            this.processQueue(null)
            
            // Retry the original request with the new token
            const config = {
                headers: this.getAuthHeaders(),
                ...options,
            }
            
            const response = await fetch(`${this.baseURL}${endpoint}`, config)
            
            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Authentication required')
                }
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error || `HTTP ${response.status}`)
            }
            
            // Handle 204 No Content responses (no JSON to parse)
            if (response.status === 204) {
                return null
            }
            
            return await response.json()
            
        } catch (error) {
            // Clear token and process queue with error
            this.setAuthToken(null)
            this.processQueue(error)
            throw new Error('Authentication required')
        } finally {
            this.isRefreshing = false
        }
    }

    // Process queued requests after token refresh
    processQueue(error) {
        this.failedQueue.forEach(({ resolve, reject, endpoint, options }) => {
            if (error) {
                reject(error)
            } else {
                resolve(this.request(endpoint, options))
            }
        })
        
        this.failedQueue = []
    }

    // Refresh the current token
    async refreshToken() {
        if (!this.authToken) {
            throw new Error('No token to refresh')
        }

        const response = await fetch(`${this.baseURL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Auth-Token': this.authToken,
                // Add CSRF token
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            }
        })

        if (!response.ok) {
            throw new Error('Token refresh failed')
        }

        const data = await response.json()
        this.setAuthToken(data.token)
        
        // console.log('Token refreshed successfully') // Commented out to reduce console clutter
        return data
    }

    // Check if token needs refreshing (80% of its lifetime)
    needsRefresh() {
        if (!this.authToken) return false
        
        const tokenSetAt = localStorage.getItem('auth_token_set_at')
        if (!tokenSetAt) return false
        
        const tokenAge = Date.now() - parseInt(tokenSetAt)
        
        // Assume web tokens last 30 days (720 hours), refresh at 80% (576 hours)
        const WEB_TOKEN_LIFETIME = 30 * 24 * 60 * 60 * 1000 // 30 days in ms
        const REFRESH_THRESHOLD = 0.8 // Refresh at 80% of lifetime
        
        return tokenAge > (WEB_TOKEN_LIFETIME * REFRESH_THRESHOLD)
    }

    // Start background token renewal
    startBackgroundRefresh() {
        // Check every hour if token needs refresh
        const CHECK_INTERVAL = 60 * 60 * 1000 // 1 hour in ms
        
        setInterval(async () => {
            if (this.needsRefresh() && !this.isRefreshing) {
                try {
                    // console.log('Background token refresh triggered') // Commented out to reduce console clutter
                    await this.refreshToken()
                } catch (error) {
                    console.warn('Background token refresh failed:', error)
                    // Don't clear token on background refresh failure - let user continue until 401
                }
            }
        }, CHECK_INTERVAL)
        
        // console.log('Background token refresh started (checking every hour)') // Commented out to reduce console clutter
    }

    // Initialize token management (call this when app starts)
    initializeTokenManagement() {
        // Check if token needs immediate refresh on app load
        if (this.needsRefresh() && this.authToken) {
            console.log('Token needs refresh on app load')
            this.refreshToken().catch(error => {
                console.warn('Initial token refresh failed:', error)
            })
        }
        
        // Start background refresh monitoring
        this.startBackgroundRefresh()
    }

    // Convenience methods
    async get(endpoint, options = {}) {
        let url = endpoint
        if (options.params) {
            const queryParams = new URLSearchParams(options.params).toString()
            url = queryParams ? `${endpoint}?${queryParams}` : endpoint
        }
        return this.request(url, { method: 'GET' })
    }

    async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        })
    }

    async patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        })
    }

    async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' })
    }

    // Specific API methods
    async getMe(paginationParams = {}) {
        const queryParams = new URLSearchParams(paginationParams).toString()
        const endpoint = queryParams ? `/auth/me?${queryParams}` : '/auth/me'
        return this.get(endpoint)
    }

    async getWorklogs(params = {}) {
        const queryParams = new URLSearchParams(params).toString()
        const endpoint = queryParams ? `/worklogs?${queryParams}` : '/worklogs'
        return this.get(endpoint)
    }

    async getWorklogsWithTotals(params = {}) {
        const queryParams = new URLSearchParams({
            ...params,
            include_totals: 'true'
        }).toString()
        return this.get(`/worklogs?${queryParams}`)
    }

    async getWorklogSuggestions(limit = null) {
        const query = limit ? `?limit=${limit}` : ''
        return this.get(`/worklog_suggestions${query}`)
    }

    async acceptWorklogSuggestion(id) {
        return this.post(`/worklog_suggestions/${id}/accept`, {})
    }

    async deleteWorklogSuggestion(id) {
        return this.delete(`/worklog_suggestions/${id}`)
    }

    async getRunningWorklog(userId) {
        // Get worklogs and filter for running ones using status field
        const response = await this.getWorklogs({ user_id: userId })
        const runningWorklogs = response.worklogs?.filter(worklog => worklog.status === 'running') || []
        return runningWorklogs.length > 0 ? runningWorklogs[0] : null
    }

    async createWorklog(worklogData) {
        // Backend uses user's stored timezone preference unless explicitly overridden in worklogData
        const response = await this.post('/worklogs', { worklog: worklogData })
        // Normalize response: backend now returns { worklogs: [...], split: boolean }
        // Handle both old format { worklog: {...} } and new format
        if (response.worklogs) {
            return response // New format
        } else if (response.worklog) {
            // Old format compatibility
            return { worklogs: [response.worklog], split: false }
        }
        return response
    }

    async stopWorklog(stopData) {
        // Rails controller expects flat parameters for stop action
        // Backend uses user's stored timezone preference unless explicitly overridden in stopData
        const response = await this.patch('/worklogs/stop', stopData)
        // Normalize response: backend now returns { worklogs: [...], split: boolean }
        // Handle both old format { worklog: {...} } and new format
        if (response.worklogs) {
            return response // New format
        } else if (response.worklog) {
            // Old format compatibility
            return { worklogs: [response.worklog], split: false }
        }
        return response
    }

    async updateWorklog(worklogId, updateData) {
        // Backend uses user's stored timezone preference unless explicitly overridden in updateData
        const response = await this.patch(`/worklogs/${worklogId}`, { worklog: updateData })
        // Normalize response: backend now returns { worklogs: [...], split: boolean }
        // Handle both old format { worklog: {...} } and new format
        if (response.worklogs) {
            return response // New format
        } else if (response.worklog) {
            // Old format compatibility
            return { worklogs: [response.worklog], split: false }
        }
        return response
    }

    async deleteWorklog(worklogId) {
        return this.delete(`/worklogs/${worklogId}`)
    }

    async deleteRunningWorklog(userId) {
        return this.delete(`/worklogs/destroy_running?user_id=${userId}`)
    }

    async getDescriptionSuggestions(userId = null) {
        const params = userId ? `?user_id=${userId}` : ''
        return this.get(`/worklogs/description_suggestions${params}`)
    }

    // Teammates API methods
    async getTeammates(params = {}) {
        const queryParams = new URLSearchParams(params).toString()
        const endpoint = queryParams ? `/teammates?${queryParams}` : '/teammates'
        return this.get(endpoint)
    }

    async getTeammate(teammateId) {
        return this.get(`/teammates/${teammateId}`)
    }

    async addTeammate(teammateData) {
        return this.post('/teammates', { teammate: teammateData })
    }

    async updateWorkspaceMember(memberId, memberData) {
        return this.patch(`/teammates/${memberId}`, { teammate: memberData })
    }

    async removeTeammate(teammateId) {
        return this.delete(`/teammates/${teammateId}`)
    }

    // Projects API methods
    async getProjects(userId = null) {
        const params = userId ? `?user_id=${userId}` : ''
        return this.get(`/projects${params}`)
    }

    async getUserProjects(userId) {
        return this.get(`/users/${userId}/projects`)
    }

    async createProject(projectData) {
        return this.post('/projects', { project: projectData })
    }

    async addUserToProject(projectId, userId, role = 'member') {
        return this.post(`/projects/${projectId}/users`, { user_id: userId, role: role })
    }

    async removeUserFromProject(projectId, userId) {
        return this.delete(`/projects/${projectId}/users/${userId}`)
    }

    // Groups API methods
    async getGroups(params = {}) {
        const queryParams = new URLSearchParams(params).toString()
        const endpoint = queryParams ? `/groups?${queryParams}` : '/groups'
        return this.get(endpoint)
    }

    async createGroup(groupData) {
        return this.post('/groups', { group: groupData })
    }

    async updateGroup(groupId, groupData) {
        return this.put(`/groups/${groupId}`, { group: groupData })
    }

    async addMemberToGroup(groupId, memberId) {
        return this.post(`/groups/${groupId}/members`, { member_id: memberId })
    }

    async removeMemberFromGroup(groupId, memberId) {
        return this.delete(`/groups/${groupId}/members/${memberId}`)
    }

    // Group Projects API methods
    async addProjectToGroup(groupId, projectData) {
        return this.post(`/groups/${groupId}/projects`, projectData)
    }

    async removeProjectFromGroup(groupId, projectId) {
        return this.delete(`/groups/${groupId}/projects/${projectId}`)
    }

    // Group Reminders API methods
    async getGroupReminders(groupId) {
        return this.get(`/groups/${groupId}/reminders`)
    }

    async createGroupReminder(groupId, reminderData) {
        return this.post(`/groups/${groupId}/reminders`, { reminder: reminderData })
    }

    async updateGroupReminder(groupId, reminderId, reminderData) {
        return this.put(`/groups/${groupId}/reminders/${reminderId}`, { reminder: reminderData })
    }

    async deleteGroupReminder(groupId, reminderId) {
        return this.delete(`/groups/${groupId}/reminders/${reminderId}`)
    }

    // Project Management API methods
    async getProject(projectId) {
        return this.get(`/projects/${projectId}`)
    }

    async updateProject(projectId, projectData) {
        return this.put(`/projects/${projectId}`, projectData)
    }

    async updateProjectTimeLock(projectId, timeLockData) {
        return this.put(`/projects/${projectId}/time_lock`, { time_lock: timeLockData })
    }

    async deleteProject(projectId) {
        return this.delete(`/projects/${projectId}`)
    }

    // Clients API methods
    async getClients(params = {}) {
        const queryParams = new URLSearchParams(params).toString()
        const endpoint = queryParams ? `/clients?${queryParams}` : '/clients'
        return this.get(endpoint)
    }

    async getClient(clientId) {
        return this.get(`/clients/${clientId}`)
    }

    async createClient(clientData) {
        return this.post('/clients', { client: clientData })
    }

    async updateClient(clientId, clientData) {
        return this.patch(`/clients/${clientId}`, { client: clientData })
    }

    async deleteClient(clientId) {
        return this.delete(`/clients/${clientId}`)
    }

    // Tasks API methods
    async getTasks(projectId) {
        return this.get(`/projects/${projectId}/tasks`)
    }

    async getAllTasks(projects) {
        try {
            const allTasks = []
            for (const project of projects) {
                const response = await this.getTasks(project.id)
                const projectTasks = response.tasks?.map(task => ({
                    ...task,
                    project_id: project.id,
                    project_name: project.name
                })) || []
                allTasks.push(...projectTasks)
            }
            return allTasks
        } catch (error) {
            console.error('Failed to fetch all tasks:', error)
            return []
        }
    }

    async createTask(projectId, taskData) {
        return this.post(`/projects/${projectId}/tasks`, { task: taskData })
    }

    async updateTask(projectId, taskId, taskData) {
        return this.patch(`/projects/${projectId}/tasks/${taskId}`, { task: taskData })
    }

    async deleteTask(projectId, taskId) {
        return this.delete(`/projects/${projectId}/tasks/${taskId}`)
    }

    // Command K API methods
    async processCommand(command) {
        return this.post('/commands/process', { command })
    }

    async getCommandSuggestions() {
        return this.get('/commands/suggestions')
    }

    async getCommandHealth() {
        return this.get('/commands/health')
    }

    // Automated Reports API methods
    async getAutomatedReports() {
        return this.get('/automated_reports')
    }

    async createAutomatedReport(reportData) {
        return this.post('/automated_reports', { automated_report: reportData })
    }

    async updateAutomatedReport(id, reportData) {
        return this.patch(`/automated_reports/${id}`, { automated_report: reportData })
    }

    async deleteAutomatedReport(id) {
        return this.delete(`/automated_reports/${id}`)
    }

    async toggleAutomatedReport(id) {
        return this.patch(`/automated_reports/${id}/toggle_active`)
    }

    async getAutomatedReportsTimeFrameOptions() {
        return this.get('/automated_reports/time_frame_options')
    }

    async getAutomatedReportsScheduleOptions() {
        return this.get('/automated_reports/schedule_options')
    }

    async previewAutomatedReport(data) {
        return this.post('/automated_reports/preview', data)
    }

    // Saved Filters API methods (if not already present)
    async getSavedFilters() {
        return this.get('/saved_filters')
    }

    async createSavedFilter(filterData) {
        return this.post('/saved_filters', { saved_filter: filterData })
    }

    async updateSavedFilter(id, filterData) {
        return this.patch(`/saved_filters/${id}`, { saved_filter: filterData })
    }

    async deleteSavedFilter(id) {
        return this.delete(`/saved_filters/${id}`)
    }

    // Workspace users API methods
    async getWorkspaceUsers() {
        return this.get('/teammates')
    }

    // Subscription / Billing API methods
    async getSubscriptionPlans() {
        return this.get('/subscriptions/plans')
    }

    async getCurrentSubscription() {
        return this.get('/subscriptions')
    }

    async createSubscription(data) {
        // data: { plan, success_url, cancel_url }
        return this.post('/subscriptions', data)
    }

    async updateSubscription(data) {
        // data: { plan?, add_seats?, remove_seats? }
        return this.patch('/subscriptions', data)
    }

    async cancelSubscription() {
        return this.delete('/subscriptions')
    }

    // Profile / Timezone API
    async getTimezone() {
        return this.get('/profile/timezone')
    }

    async updateTimezone(timezone, confirmed = false) {
        return this.patch('/profile/timezone', { timezone, confirmed })
    }
}

// Create a singleton instance
const apiClient = new ApiClient()

export default apiClient
